"use client";

import { useState } from "react";

import type { LegendEntry } from "@/components/charts/HorizontalCategoricalBar";

export type ProportionSegment<M = unknown> = {
  key: string;
  label: string;
  value: number;
  color: string;
  meta?: M;
};

type Props<M = unknown> = {
  segments: ProportionSegment<M>[];
  height?: number;
  /** Tooltip body. If omitted, just label + value + percent. */
  tooltipDescription?: (s: ProportionSegment<M>) => string | null;
  valueFormatter: (v: number, s: ProportionSegment<M>) => string;
  legend?: LegendEntry[];
  /** Below this width fraction the in-bar label is hidden (still in tooltip). */
  inlineLabelMinFraction?: number;
};

export function ProportionBar<M = unknown>({
  segments,
  height = 56,
  tooltipDescription,
  valueFormatter,
  legend,
  inlineLabelMinFraction = 0.08,
}: Props<M>) {
  const [hover, setHover] = useState<{
    index: number;
    px: number;
    py: number;
  } | null>(null);

  const total = segments.reduce((s, x) => s + x.value, 0) || 1;

  return (
    <div className="relative w-full">
      <div
        className="flex w-full overflow-hidden border border-rule"
        style={{ height }}
        onMouseLeave={() => setHover(null)}
      >
        {segments.map((seg, i) => {
          const frac = seg.value / total;
          const showInline = frac >= inlineLabelMinFraction;
          return (
            <div
              key={`${i}-${seg.key}`}
              className="relative flex items-center justify-center px-2 text-xs font-medium"
              style={{
                width: `${frac * 100}%`,
                backgroundColor: seg.color,
                color: readableTextOn(seg.color),
              }}
              onMouseEnter={(e) => {
                const host = e.currentTarget.parentElement?.parentElement;
                const r = host?.getBoundingClientRect();
                if (!r) return;
                setHover({
                  index: i,
                  px: e.clientX - r.left,
                  py: e.clientY - r.top,
                });
              }}
              onMouseMove={(e) => {
                const host = e.currentTarget.parentElement?.parentElement;
                const r = host?.getBoundingClientRect();
                if (!r) return;
                setHover({
                  index: i,
                  px: e.clientX - r.left,
                  py: e.clientY - r.top,
                });
              }}
            >
              {showInline && (
                <div className="text-center leading-tight">
                  <div
                    className="cs-num text-2xl"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {Math.round(frac * 100)}%
                  </div>
                  <div className="mt-0.5 text-[12px] font-medium leading-tight">
                    {seg.label}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hover && (
        <div
          className="pointer-events-none absolute border border-rule bg-bg text-ink-2 px-3 py-2 text-sm shadow-md max-w-[320px] z-10"
          style={{
            left: hover.px + 12,
            top: hover.py + 12,
          }}
        >
          {(() => {
            const seg = segments[hover.index];
            const frac = seg.value / total;
            const description = tooltipDescription?.(seg);
            return (
              <>
                <div className="font-medium">{seg.label}</div>
                <div className="cs-mono text-xs mt-0.5">
                  {valueFormatter(seg.value, seg)} ·{" "}
                  {(frac * 100).toFixed(1)}%
                </div>
                {description && (
                  <div className="mt-1.5 text-xs leading-relaxed text-ink-2">
                    {description}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {legend && legend.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          {legend.map((entry, i) => (
            <span
              key={`${i}-${entry.color}-${entry.label}`}
              className="flex items-center gap-2"
            >
              <span
                aria-hidden
                className="inline-block h-3 w-3"
                style={{ background: entry.color }}
              />
              <span>{entry.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function readableTextOn(fill: string): string {
  const rgb = parseColor(fill);
  if (!rgb) return "#fafaf7";
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  // Tile-relative: dark text on a light tile, light text on a dark tile.
  // Both themes pick from the same hex pair because the decision keys off
  // the tile colour, not the page bg.
  return lum > 0.62 ? "#3a3a37" : "#fafaf7";
}

function parseColor(s: string): { r: number; g: number; b: number } | null {
  const m = s.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  const h = s.match(/^#([0-9a-f]{6})$/i);
  if (h) {
    const n = parseInt(h[1], 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  return null;
}
